import { useState } from "react";
import { useParams, Link } from "react-router";
import { useShopProduct, useShopProducts } from "@/lib/api.ts";
import { ImageGallery } from "@/components/ImageGallery.tsx";
import { Accordion } from "@/components/Accordion.tsx";
import { WishlistButton } from "@/components/WishlistButton.tsx";
import { ProductCard } from "@/components/ProductCard.tsx";
import { toUrlFriendly, stripHtml, getLowestPrice, normalizePrice } from "@/lib/utils.ts";
import { Star, Check, ArrowRight } from "lucide-react";
import { useDocumentHead } from "@/hooks/useDocumentHead.ts";
import type { AccordionItem } from "@/components/Accordion.tsx";

declare global {
  interface Window {
    EF?: { click: (opts: Record<string, unknown>) => void };
  }
}

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useShopProduct(slug ?? "");
  const { data: allData } = useShopProducts();
  const [openSection, setOpenSection] = useState<{ id: string; t: number } | null>(null);

  // SEO meta tags — must be called before early returns (hooks can't be conditional)
  const productName = data?.product?.name ?? slug ?? "";
  const rawDescForMeta = stripHtml(data?.research?.productData?.description || "");
  const metaDesc = rawDescForMeta.length > 160 ? rawDescForMeta.slice(0, 157).replace(/\s+\S*$/, "") + "..." : rawDescForMeta || `Shop ${productName} at The Secret Trends`;
  const ogImg = data?.product?.imageUrl || data?.assets?.[0]?.localPath || data?.assets?.[0]?.previewUrl || undefined;
  useDocumentHead({
    title: productName || "Product",
    description: metaDesc,
    ogImage: ogImg || undefined,
    ogType: "product",
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-heading border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1360px] px-6 py-16 text-center lg:px-10">
        <h1 className="text-2xl font-medium text-heading">Product not found</h1>
        <Link to="/" className="mt-4 inline-block text-sm text-text-muted hover:underline">
          &larr; Back to shop
        </Link>
      </div>
    );
  }

  const { product, assets, blogPosts, research } = data;
  const features = Array.isArray(research?.features) ? (research.features as string[]) : [];
  const benefits = Array.isArray(research?.benefits) ? (research.benefits as string[]) : [];
  const testimonials = research?.productData?.testimonials ?? [];
  const faqs = research?.productData?.faqs ?? [];
  const bundles = research?.pricing?.bundles ?? [];
  const contentSections = research?.productData?.contentSections ?? [];
  // AI-generated original analytical content (scripts/enrich-products.mjs).
  // 5 sections of editorial analysis displayed as a collapsible accordion
  // — gives buyers depth without forcing it on quick-purchase users.
  const enrichedSections = research?.productData?.enrichedContent?.sections ?? [];
  const scrapedImages = (research?.productData?.images ?? []).map((img) =>
    typeof img === "string" ? { url: img } : { url: img.url, alt: img.alt }
  );
  const scrapedVideos = research?.productData?.videos ?? [];

  // Use research description (from scraped landing page) first, then shopDescription as fallback
  const rawDesc = research?.productData?.description || "";
  const description = stripHtml(rawDesc);
  // Short description for above-fold — first 200 chars
  const shortDesc = description.length > 200 ? description.slice(0, 200).replace(/\s+\S*$/, "") + "..." : description;

  // Price with "Starting at" for bundles
  const priceInfo = getLowestPrice(product.price, bundles);

  // Related products from same category
  const allProducts = allData ?? {};
  const categoryProducts = product.category
    ? (allProducts[product.category] ?? []).filter((p) => p.id !== product.id)
    : [];

  function handleBuyNow() {
    try {
      window.EF?.click({});
    } catch {
      // Everflow SDK may not be loaded
    }
    if (product.url) {
      window.open(product.url, "_blank", "noopener,noreferrer");
    }
  }

  // Merge features + benefits into one list for compact display
  const highlights = [...features, ...benefits];

  // Build accordion items
  const accordionItems: AccordionItem[] = [];

  // "Product Details" first — with rich content sections + description
  if (contentSections.length > 0 || description) {
    accordionItems.push({
      title: "Product Details",
      defaultOpen: true,
      content: (
        <div>
          {contentSections.length > 0 && (
            <div className="space-y-12">
              {contentSections.map((section, i) => {
                const sectionImage = scrapedImages[i];
                const isReversed = i % 2 === 1;
                return (
                  <div
                    key={i}
                    className={`flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10 ${
                      isReversed ? "lg:flex-row-reverse" : ""
                    }`}
                  >
                    {sectionImage && (
                      <div className="flex-shrink-0 lg:w-2/5">
                        <img
                          src={sectionImage.url}
                          alt={sectionImage.alt || section.heading}
                          className="w-full object-cover"
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (img.naturalWidth < 200 || img.naturalHeight < 200) {
                              const wrapper = img.parentElement;
                              if (wrapper) wrapper.style.display = "none";
                            }
                          }}
                          onError={(e) => {
                            const wrapper = (e.target as HTMLElement).parentElement;
                            if (wrapper) wrapper.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                    <div className={sectionImage ? "" : "w-full"}>
                      <h3 className="mb-2 text-lg font-medium text-heading">
                        {section.heading}
                      </h3>
                      <p className="text-[15px] leading-[1.7] text-text-muted">
                        {section.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {description && contentSections.length > 0 && (
            <hr className="my-10 border-border" />
          )}
          {description && (
            <p className="max-w-prose text-[15px] leading-[1.7] text-text-muted">
              {description}
            </p>
          )}
        </div>
      ),
    });
  }

  if (enrichedSections.length > 0) {
    accordionItems.push({
      title: "In-depth review & analysis",
      content: (
        <div className="space-y-8">
          {enrichedSections.map((section, i) => (
            <div key={i}>
              <h3 className="mb-3 text-lg font-medium text-heading">
                {section.heading}
              </h3>
              <p className="text-[15px] leading-[1.7] text-text-muted">
                {section.body}
              </p>
            </div>
          ))}
        </div>
      ),
    });
  }

  if (highlights.length > 0) {
    accordionItems.push({
      title: "Features & Benefits",
      content: (
        <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          {highlights.slice(0, 12).map((item, i) => {
            const text = typeof item === "string" ? item : JSON.stringify(item);
            return (
              <div key={i} className="flex items-start gap-3">
                <Check size={16} className="mt-0.5 flex-shrink-0 text-heading" />
                <span className="text-[15px] leading-relaxed text-text-muted">
                  {text}
                </span>
              </div>
            );
          })}
        </div>
      ),
    });
  }

  if (testimonials.length > 0) {
    accordionItems.push({
      id: "section-reviews",
      title: "Customer Reviews",
      content: (
        <div className="space-y-6">
          {testimonials.slice(0, 6).map((t, i) => (
            <div key={i} className="border-b border-border pb-6 last:border-0">
              <div className="mb-2 flex gap-0.5">
                {Array.from({ length: 5 }, (_, j) => (
                  <Star
                    key={j}
                    size={14}
                    className="fill-heading text-heading"
                  />
                ))}
              </div>
              <p className="text-[15px] leading-relaxed text-text-muted">
                "{t.text}"
              </p>
              {t.author && (
                <p className="mt-2 text-xs font-medium text-heading">
                  {t.author}
                </p>
              )}
            </div>
          ))}
        </div>
      ),
    });
  }

  if (faqs.length > 0) {
    accordionItems.push({
      title: "FAQ",
      content: (
        <div className="space-y-6">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-border pb-6 last:border-0">
              <h4 className="text-[15px] font-medium text-heading">{faq.question}</h4>
              <p className="mt-2 text-[15px] leading-relaxed text-text-muted">
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      ),
    });
  }

  if (blogPosts.length > 0) {
    accordionItems.push({
      title: "Related Articles",
      content: (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {blogPosts.map((post) => (
            <a
              key={post.id}
              href={`/blog/${post.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group border border-border transition-shadow hover:shadow-md"
            >
              {post.heroImagePath && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.heroImagePath}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-4">
                <h4 className="text-sm font-medium text-heading line-clamp-2 group-hover:underline">
                  {post.title}
                </h4>
                {post.publishedAt && (
                  <p className="mt-1 text-xs text-text-muted">
                    {new Date(post.publishedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      ),
    });
  }


  return (
    <>
      <div className="mx-auto max-w-[1360px] px-6 py-8 lg:px-10">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center gap-2 text-xs text-text-muted">
          <Link to="/" className="hover:text-heading">
            Home
          </Link>
          <span>/</span>
          {product.category && (
            <>
              <Link
                to={`/collection/${toUrlFriendly(product.category)}`}
                className="hover:text-heading"
              >
                {product.category}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="text-heading">{product.name}</span>
        </nav>

        {/* Two-column layout */}
        <div className="grid gap-10 lg:grid-cols-[55%_45%] lg:gap-16">
          {/* Left: Image Gallery */}
          <div>
            <ImageGallery
              assets={assets}
              fallbackUrl={product.imageUrl}
              productName={product.name}
              scrapedImages={scrapedImages}
              scrapedVideos={scrapedVideos}
            />
          </div>

          {/* Right: Product Info */}
          <div>
            <h1 className="text-[32px] font-medium leading-tight tracking-tight text-heading lg:text-[36px]">
              {product.name}
            </h1>

            {/* Star rating */}
            {testimonials.length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star key={i} size={14} className="fill-heading text-heading" />
                  ))}
                </div>
                <button
                  onClick={() => setOpenSection({ id: "section-reviews", t: Date.now() })}
                  className="text-xs text-text-muted hover:underline"
                >
                  See reviews
                </button>
              </div>
            )}

            {/* Description — use research description, not shopDescription */}
            {shortDesc && (
              <p className="mt-4 text-[15px] leading-[1.7] text-text-muted">
                {shortDesc}
              </p>
            )}

            {/* Price — with "Starting at" for bundles */}
            {priceInfo && (
              <div className="mt-6">
                <p className="text-2xl font-semibold text-heading">
                  {priceInfo.label && (
                    <span className="mr-1 text-sm font-normal text-text-muted">
                      {priceInfo.label}
                    </span>
                  )}
                  {priceInfo.amount}
                </p>
                <p className="mt-1 text-xs text-text-muted">Free shipping on most orders</p>
              </div>
            )}

            {/* Bundles preview */}
            {bundles.length > 1 && (
              <div className="mt-4 space-y-2">
                {bundles.slice(0, 3).map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between border border-border px-4 py-3 text-sm"
                  >
                    <span className="text-heading">{b.name || `Option ${i + 1}`}</span>
                    {b.price && (
                      <span className="font-medium text-heading">{normalizePrice(b.price)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Buy Now */}
            {product.url && (
              <button
                onClick={handleBuyNow}
                className="mt-8 flex w-full cursor-pointer items-center justify-center gap-2 bg-heading py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-heading/90"
              >
                Buy Now
                <ArrowRight size={16} />
              </button>
            )}

            {/* Wishlist */}
            <div className="mt-3">
              <WishlistButton productId={product.id} variant="full" />
            </div>

          </div>
        </div>

        {/* Accordion Sections */}
        {accordionItems.length > 0 && (
          <div className="mt-20">
            <Accordion items={accordionItems} openSectionId={openSection?.id ?? null} openKey={openSection?.t} />
          </div>
        )}

        {/* Related Products */}
        {categoryProducts.length > 0 && (
          <section className="mt-20 pb-8">
            <h2 className="mb-8 text-2xl font-medium tracking-tight text-heading">
              You Might Also Like
            </h2>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
              {categoryProducts.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky Mobile CTA */}
      {product.url && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white px-4 py-3 lg:hidden">
          <div className="flex items-center justify-between gap-4">
            {priceInfo && (
              <div>
                {priceInfo.label && (
                  <span className="block text-[10px] text-text-muted">{priceInfo.label}</span>
                )}
                <p className="text-lg font-semibold text-heading">
                  {priceInfo.amount}
                </p>
              </div>
            )}
            <button
              onClick={handleBuyNow}
              className="flex flex-1 cursor-pointer items-center justify-center gap-2 bg-heading py-3 text-sm font-medium tracking-wide text-white"
            >
              Buy Now
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
